import type {
  EnhancedGamepad,
  GamepadButtonEvent,
  GamepadAxisEvent,
  GamepadConnectionEvent,
  GamepadListener,
  GamepadEventType,
  GamepadManagerConfig,
} from "./types";
import {
  createEnhancedGamepad,
  getConnectedGamepads,
  generateListenerId,
  detectDeviceType,
  enhancedGamepadsToArray,
  applyDeadzone,
} from "./utils";

export class GamepadPollingManager {
  private static instance: GamepadPollingManager | null = null;
  private static readonly AXIS_EMIT_THRESHOLD = 0.05;
  private config: Required<GamepadManagerConfig>;
  private isPolling = false;
  private pollingId: number | null = null;
  private useRequestAnimationFrame = true;

  // State management
  private gamepadStates: Map<number, EnhancedGamepad> = new Map(); // Key: sequential index
  private previousStates: Map<number, EnhancedGamepad> = new Map(); // Key: sequential index

  // Index mapping: browser index -> sequential index -> mapped index
  private gamepadIndexMap = new Map<number, number>(); // browser -> sequential
  private nextSequentialIndex = 0;
  private availableSequentialIndices = new Set<number>();

  // Track device types by sequential index
  private deviceTypes = new Map<number, "controller" | "remote">();

  // Cache of the detected gamepad type (xbox/nintendo/sony/remote)
  private deviceTypeCache = new Map<
    number,
    "xbox" | "nintendo" | "sony" | "remote"
  >();

  // Mapping cache for performance (sequential -> mapped)
  private mappingCache = new Map<number, number>();
  private slot0IsRemote = false;

  // Debounce timers for gamepadconnected events (avoids stale gamepad snapshots)
  private pendingConnects = new Map<number, ReturnType<typeof setTimeout>>();

  // Event system
  private listeners: Map<string, GamepadListener> = new Map();
  private listenersByType: Map<GamepadEventType, Set<string>> = new Map();
  private sortedListenersCache: Map<GamepadEventType, GamepadListener[]> =
    new Map();

  // Performance tracking
  private lastPollTime = 0;
  private pollCount = 0;
  private debug = false;

  private constructor(config: GamepadManagerConfig = {}) {
    this.config = {
      maxGamepads: config.maxGamepads ?? 4,
      pollRate: config.pollRate ?? 60,
      deadZone: config.deadZone ?? 0.05,
      debug: config.debug ?? false,
    };

    this.debug = this.config.debug;

    // Initialize event type maps
    (["button", "axis", "connect", "disconnect"] as GamepadEventType[]).forEach(
      (type) => {
        this.listenersByType.set(type, new Set());
      },
    );

    // Listen for browser gamepad events
    this.setupBrowserEventListeners();

    // Check for pre-connected gamepads
    this.checkForConnectedGamepads();

    if (this.debug) {
      console.log(
        "[GamepadPollingManager] Initialized with config:",
        this.config,
      );
    }
  }

  /**
   * Get singleton instance
   */
  public static getInstance(
    config?: GamepadManagerConfig,
  ): GamepadPollingManager {
    if (!GamepadPollingManager.instance) {
      GamepadPollingManager.instance = new GamepadPollingManager(config);
    }
    return GamepadPollingManager.instance;
  }

  /**
   * Destroy singleton instance
   */
  public static destroyInstance(): void {
    if (GamepadPollingManager.instance) {
      GamepadPollingManager.instance.destroy();
      GamepadPollingManager.instance = null;
    }
  }

  /**
   * Start polling for gamepad input
   */
  public startPolling(): void {
    if (this.isPolling) {
      if (this.debug) {
        console.log("[GamepadPollingManager] Already polling");
      }
      return;
    }

    this.isPolling = true;
    this.lastPollTime = performance.now();

    if (this.debug) {
      console.log(
        `[GamepadPollingManager] Started polling at ${this.config.pollRate}Hz`,
      );
    }

    if (this.useRequestAnimationFrame) {
      this.pollingId = requestAnimationFrame(() => this.pollWithRAF());
    } else {
      const intervalMs = 1000 / this.config.pollRate;
      this.pollingId = window.setInterval(() => this.poll(), intervalMs);
    }
  }

  /**
   * Stop polling for gamepad input
   */
  public stopPolling(): void {
    if (!this.isPolling) {
      if (this.debug) {
        console.log("[GamepadPollingManager] Not currently polling");
      }
      return;
    }

    this.isPolling = false;

    if (this.pollingId !== null) {
      if (this.useRequestAnimationFrame) {
        cancelAnimationFrame(this.pollingId);
      } else {
        clearInterval(this.pollingId);
      }
      this.pollingId = null;
    }

    if (this.debug) {
      console.log(
        `[GamepadPollingManager] Stopped polling. Total polls: ${this.pollCount}`,
      );
    }
  }

  /**
   * Add event listener
   */
  public addEventListener(
    type: GamepadEventType,
    callback: (event: any) => void,
    options: { id?: string; priority?: number } = {},
  ): string {
    const id = options.id || generateListenerId(type);

    const listener: GamepadListener = {
      id,
      type,
      callback,
      priority: options.priority ?? 0,
    };

    this.listeners.set(id, listener);
    this.listenersByType.get(type)?.add(id);
    this.sortedListenersCache.delete(type);

    if (this.debug) {
      console.log(`[GamepadPollingManager] Added ${type} listener: ${id}`);
    }

    return id;
  }

  /**
   * Remove event listener
   */
  public removeEventListener(id: string): boolean {
    const listener = this.listeners.get(id);
    if (!listener) return false;

    this.listeners.delete(id);
    this.listenersByType.get(listener.type)?.delete(id);
    this.sortedListenersCache.delete(listener.type);

    if (this.debug) {
      console.log(`[GamepadPollingManager] Removed listener: ${id}`);
    }

    return true;
  }

  /**
   * Get enhanced gamepad by sequential index
   */
  public getEnhancedGamepad(
    sequentialIndex: number,
  ): EnhancedGamepad | undefined {
    return this.gamepadStates.get(sequentialIndex);
  }

  /**
   * Get enhanced gamepad by mapped index
   */
  public getEnhancedGamepadByMappedIndex(
    mappedIndex: number,
  ): EnhancedGamepad | undefined {
    for (const enhanced of this.gamepadStates.values()) {
      if (enhanced.mappedIndex === mappedIndex) {
        return enhanced;
      }
    }
    return undefined;
  }

  /**
   * Get all enhanced gamepads
   */
  public getAllEnhancedGamepads(): EnhancedGamepad[] {
    return Array.from(this.gamepadStates.values());
  }

  /**
   * Get gamepad as browser Gamepad object by sequential index
   */
  public getGamepadObject(sequentialIndex: number): Gamepad | null {
    const enhanced = this.gamepadStates.get(sequentialIndex);
    return enhanced ? enhanced.gamepad : null;
  }

  /**
   * Get gamepad as browser Gamepad object by mapped index
   */
  public getGamepadObjectByMappedIndex(mappedIndex: number): Gamepad | null {
    const enhanced = this.getEnhancedGamepadByMappedIndex(mappedIndex);
    return enhanced ? enhanced.gamepad : null;
  }

  /**
   * Get all gamepads as browser Gamepad array (like navigator.getGamepads())
   */
  public getGamepads(): (Gamepad | null)[] {
    const enhanced = this.getAllEnhancedGamepads();
    return enhancedGamepadsToArray(enhanced);
  }

  /**
   * Get gamepad index mapping info
   */
  public getIndexMappingInfo() {
    return {
      browserToSequential: new Map(this.gamepadIndexMap),
      sequentialToMapped: new Map(this.mappingCache),
      deviceTypes: new Map(this.deviceTypes),
      slot0IsRemote: this.slot0IsRemote,
    };
  }

  /**
   * Set polling method (RAF vs setInterval)
   */
  public setPollingMethod(useRAF: boolean): void {
    if (this.useRequestAnimationFrame === useRAF) return;

    const wasPolling = this.isPolling;
    if (wasPolling) {
      this.stopPolling();
    }

    this.useRequestAnimationFrame = useRAF;

    if (wasPolling) {
      this.startPolling();
    }

    if (this.debug) {
      console.log(
        `[GamepadPollingManager] Switched to ${
          useRAF ? "RAF" : "setInterval"
        } polling`,
      );
    }
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<GamepadManagerConfig>): void {
    const wasPolling = this.isPolling;
    const pollRateChanged =
      config.pollRate && config.pollRate !== this.config.pollRate;

    if (wasPolling && pollRateChanged) {
      this.stopPolling();
    }

    this.config = { ...this.config, ...config };
    this.debug = this.config.debug;

    if (wasPolling && pollRateChanged) {
      this.startPolling();
    }

    if (this.debug) {
      console.log("[GamepadPollingManager] Updated config:", this.config);
    }
  }

  // ==================== GAMEPAD INDEX MAPPING ====================

  /**
   * Assign sequential index with controller priority for slot 0
   */
  private assignSequentialIndex(isController: boolean): number {
    // Get next available sequential index
    let sequentialIndex: number;
    if (this.availableSequentialIndices.size > 0) {
      sequentialIndex = Math.min(...this.availableSequentialIndices);
      this.availableSequentialIndices.delete(sequentialIndex);
    } else {
      sequentialIndex = this.nextSequentialIndex++;
    }

    // Track device type
    this.deviceTypes.set(
      sequentialIndex,
      isController ? "controller" : "remote",
    );

    // Rebuild mapping cache
    this.rebuildMappingCache();

    return sequentialIndex;
  }

  /**
   * Rebuild mapping cache after device changes
   */
  private rebuildMappingCache(): void {
    this.mappingCache.clear();
    this.slot0IsRemote = this.deviceTypes.get(0) === "remote";

    if (this.slot0IsRemote) {
      // Get controllers sorted by sequential index
      const controllers = Array.from(this.deviceTypes.entries())
        .filter(([_, type]) => type === "controller")
        .map(([index, _]) => index)
        .sort((a, b) => a - b);

      // Map all devices
      for (const [sequentialIndex, deviceType] of this.deviceTypes.entries()) {
        if (sequentialIndex === 0) {
          this.mappingCache.set(sequentialIndex, 0); // Remote stays at 0
        } else if (deviceType === "controller") {
          const controllerPosition = controllers.indexOf(sequentialIndex);
          this.mappingCache.set(sequentialIndex, controllerPosition);
        } else {
          this.mappingCache.set(sequentialIndex, sequentialIndex); // Other remotes keep sequential
        }
      }
    } else {
      // Controller at slot 0: keep sequential indexes
      for (const sequentialIndex of this.deviceTypes.keys()) {
        this.mappingCache.set(sequentialIndex, sequentialIndex);
      }
    }
  }

  /**
   * Get mapped index for a sequential index
   */
  private getMappedIndex(sequentialIndex: number): number {
    return this.mappingCache.get(sequentialIndex) ?? sequentialIndex;
  }

  /**
   * Reset all mapping state
   */
  private resetMapping(): void {
    this.gamepadIndexMap.clear();
    this.nextSequentialIndex = 0;
    this.availableSequentialIndices.clear();
    this.deviceTypes.clear();
    this.deviceTypeCache.clear();
    this.mappingCache.clear();
    this.slot0IsRemote = false;

    if (this.debug) {
      console.log("[GamepadPollingManager] Reset mapping state");
    }
  }

  // ==================== BROWSER EVENT LISTENERS ====================

  /**
   * Setup browser gamepad event listeners
   */
  private setupBrowserEventListeners(): void {
    const handleConnect = (event: GamepadEvent) => {
      const browserIndex = event.gamepad.index;

      // Cancel any existing pending connect for this index
      const existing = this.pendingConnects.get(browserIndex);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        this.pendingConnects.delete(browserIndex);

        // Read a fresh snapshot — event.gamepad can be stale on some browsers
        const gamepad = navigator.getGamepads()[browserIndex];
        if (!gamepad) return;

        // Skip if already mapped
        if (this.gamepadIndexMap.has(gamepad.index)) return;

        // Determine device type
        const deviceType = detectDeviceType(gamepad.id);
        const isController = deviceType !== "remote";

        // Map browser index to sequential index
        const sequentialIndex = this.assignSequentialIndex(isController);
        this.gamepadIndexMap.set(gamepad.index, sequentialIndex);
        this.deviceTypeCache.set(sequentialIndex, deviceType);

        // Get mapped index
        const mappedIndex = this.getMappedIndex(sequentialIndex);

        // Create enhanced gamepad with all indices
        const enhanced = createEnhancedGamepad(
          gamepad,
          sequentialIndex,
          mappedIndex,
          this.config.deadZone,
          deviceType,
        );

        // Store by sequential index
        this.gamepadStates.set(sequentialIndex, enhanced);

        if (this.debug) {
          console.log(
            `[GamepadPollingManager] Gamepad connected: browser:${gamepad.index} -> sequential:${sequentialIndex} -> mapped:${mappedIndex} (${deviceType})`,
          );
        }

        this.emitEvent("connect", {
          enhanced,
          connected: true,
        } as GamepadConnectionEvent);

        // Start polling if not already polling and we have connected gamepads
        if (!this.isPolling && this.gamepadStates.size > 0) {
          this.startPolling();
        }
      }, 200);

      this.pendingConnects.set(browserIndex, timer);
    };

    const handleDisconnect = (event: GamepadEvent) => {
      const gamepad = event.gamepad;

      // Cancel any pending connect debounce for this index
      const pending = this.pendingConnects.get(gamepad.index);
      if (pending) {
        clearTimeout(pending);
        this.pendingConnects.delete(gamepad.index);
      }

      // Get the sequential index for this gamepad
      const sequentialIndex = this.gamepadIndexMap.get(gamepad.index);
      if (sequentialIndex === undefined) return;

      // Get mapped index before cleanup
      const mappedIndex = this.getMappedIndex(sequentialIndex);
      const deviceType = this.deviceTypes.get(sequentialIndex) || "remote";
      const enhanced = this.gamepadStates.get(sequentialIndex);

      if (this.debug) {
        console.log(
          `[GamepadPollingManager] Gamepad disconnected: browser:${gamepad.index} -> sequential:${sequentialIndex} -> mapped:${mappedIndex} (${deviceType})`,
        );
      }

      // Clean up state
      this.gamepadStates.delete(sequentialIndex);
      this.previousStates.delete(sequentialIndex);
      this.deviceTypes.delete(sequentialIndex);
      this.deviceTypeCache.delete(sequentialIndex);
      this.availableSequentialIndices.add(sequentialIndex);

      // Rebuild mapping cache after device removal
      this.rebuildMappingCache();

      // Clear the mapping for this specific gamepad
      this.gamepadIndexMap.delete(gamepad.index);

      if (enhanced) {
        this.emitEvent("disconnect", {
          enhanced,
          connected: false,
        } as GamepadConnectionEvent);
      }

      // Stop polling and reset if no gamepads are connected
      if (this.gamepadStates.size === 0) {
        if (this.isPolling) {
          this.stopPolling();
        }
        this.resetMapping();
      }
    };

    window.addEventListener("gamepadconnected", handleConnect);
    window.addEventListener("gamepaddisconnected", handleDisconnect);
  }

  /**
   * RAF-based polling
   */
  private pollWithRAF(): void {
    if (!this.isPolling) return;

    const now = performance.now();
    const interval = 1000 / this.config.pollRate;

    if (now - this.lastPollTime >= interval) {
      this.poll();
      this.lastPollTime = now;
    }

    this.pollingId = requestAnimationFrame(() => this.pollWithRAF());
  }

  /**
   * Poll gamepad states and emit events.
   *
   * Diffs raw button/axis values against the previous snapshot and emits
   * change events in a single pass, instead of separate
   * "has anything changed" / "what changed" / "rebuild snapshot" scans.
   */
  private poll(): void {
    if (!this.isPolling) return;

    this.pollCount++;
    const connectedGamepads = getConnectedGamepads();

    // Check each browser gamepad slot
    for (
      let browserIndex = 0;
      browserIndex < connectedGamepads.length;
      browserIndex++
    ) {
      const gamepad = connectedGamepads[browserIndex];
      if (!gamepad) continue;

      // Get sequential index for this browser index
      const sequentialIndex = this.gamepadIndexMap.get(browserIndex);
      if (sequentialIndex === undefined) continue; // Not mapped yet

      const previousState = this.gamepadStates.get(sequentialIndex);
      const previousGamepad = previousState?.gamepad;

      let changed =
        !previousGamepad ||
        previousGamepad.connected !== gamepad.connected ||
        previousGamepad.buttons.length !== gamepad.buttons.length ||
        previousGamepad.axes.length !== gamepad.axes.length;

      const changedButtons: { index: number; value: number }[] = [];
      const changedAxes: { index: number; value: number }[] = [];

      for (let i = 0; i < gamepad.buttons.length; i++) {
        const btn = gamepad.buttons[i];
        const prevBtn = previousGamepad?.buttons[i];

        if (
          !prevBtn ||
          btn.value !== prevBtn.value ||
          btn.pressed !== prevBtn.pressed ||
          btn.touched !== prevBtn.touched
        ) {
          changed = true;
          if (prevBtn && btn.value !== prevBtn.value) {
            changedButtons.push({ index: i, value: btn.value });
          }
        }
      }

      for (let i = 0; i < gamepad.axes.length; i++) {
        const value = applyDeadzone(gamepad.axes[i], this.config.deadZone);
        const prevValue = previousGamepad?.axes[i];

        if (prevValue === undefined || value !== prevValue) {
          changed = true;
          if (
            prevValue !== undefined &&
            Math.abs(value - prevValue) >
              GamepadPollingManager.AXIS_EMIT_THRESHOLD
          ) {
            changedAxes.push({ index: i, value });
          }
        }
      }

      if (!changed) continue;

      const mappedIndex = this.getMappedIndex(sequentialIndex);

      // Create enhanced gamepad with all indices
      const enhanced = createEnhancedGamepad(
        gamepad,
        sequentialIndex,
        mappedIndex,
        this.config.deadZone,
        this.deviceTypeCache.get(sequentialIndex),
      );

      // Update state (by sequential index)
      this.gamepadStates.set(sequentialIndex, enhanced);

      if (previousGamepad) {
        for (const { index, value } of changedButtons) {
          const event: GamepadButtonEvent = {
            enhanced,
            buttonIndex: index,
            value,
            pressed: value > 0,
          };
          this.emitEvent("button", event);
        }

        for (const { index, value } of changedAxes) {
          const event: GamepadAxisEvent = {
            enhanced,
            axisIndex: index,
            value,
          };
          this.emitEvent("axis", event);
        }
      }
    }
  }

  /**
   * Emit event to listeners
   */
  private emitEvent(type: GamepadEventType, event: any): void {
    const listenerIds = this.listenersByType.get(type);
    if (!listenerIds || listenerIds.size === 0) return;

    let listeners = this.sortedListenersCache.get(type);
    if (!listeners) {
      listeners = Array.from(listenerIds)
        .map((id) => this.listeners.get(id))
        .filter((l): l is GamepadListener => Boolean(l))
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
      this.sortedListenersCache.set(type, listeners);
    }

    // Call listeners
    for (const listener of listeners) {
      try {
        listener.callback(event);
      } catch (error) {
        console.error(
          `[GamepadPollingManager] Error in ${type} listener ${listener.id}:`,
          error,
        );
      }
    }
  }

  /**
   * Scan for already-connected gamepads on initialization
   */
  private checkForConnectedGamepads(): void {
    try {
      const connectedGamepads = getConnectedGamepads();
      const nonNullGamepads = connectedGamepads.filter(
        (gp) => gp !== null,
      ) as Gamepad[];

      if (this.debug) {
        console.log(
          `[GamepadPollingManager] Scanning for connected gamepads, found ${nonNullGamepads.length}`,
        );
      }

      // Handle each connected gamepad
      for (const gamepad of nonNullGamepads) {
        const deviceType = detectDeviceType(gamepad.id);
        const isController = deviceType !== "remote";

        if (this.debug) {
          console.log(
            `[GamepadPollingManager] Found pre-connected gamepad: ${gamepad.id} (browser index ${gamepad.index}) - ${deviceType}`,
          );
        }

        // Map browser index to sequential index
        const sequentialIndex = this.assignSequentialIndex(isController);
        this.gamepadIndexMap.set(gamepad.index, sequentialIndex);
        this.deviceTypeCache.set(sequentialIndex, deviceType);

        // Get mapped index
        const mappedIndex = this.getMappedIndex(sequentialIndex);

        // Create enhanced gamepad
        const enhanced = createEnhancedGamepad(
          gamepad,
          sequentialIndex,
          mappedIndex,
          this.config.deadZone,
          deviceType,
        );

        // Store by sequential index
        this.gamepadStates.set(sequentialIndex, enhanced);

        // Emit connect event
        this.emitEvent("connect", {
          enhanced,
          connected: true,
        } as GamepadConnectionEvent);
      }

      // Start polling if we have connected gamepads
      if (nonNullGamepads.length > 0 && !this.isPolling) {
        this.startPolling();
      }
    } catch (error) {
      console.error(
        "[GamepadPollingManager] Error scanning for connected gamepads:",
        error,
      );
    }
  }

  /**
   * Clean up resources
   */
  private destroy(): void {
    this.stopPolling();
    for (const timer of this.pendingConnects.values()) clearTimeout(timer);
    this.pendingConnects.clear();
    this.listeners.clear();
    this.listenersByType.clear();
    this.sortedListenersCache.clear();
    this.gamepadStates.clear();
    this.previousStates.clear();
    this.resetMapping();

    if (this.debug) {
      console.log("[GamepadPollingManager] Destroyed");
    }
  }
}
